import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { get, truncate } from 'lodash'

import { EntityAuditLogEntry } from '@/schema'
import { SYSTEM_USER_ID } from '@/utils/constants'

import {
  AuditLogAction,
  formatEntityType,
  formatField,
  formatFieldValue,
  formatLogType,
  normalizeLogValue,
  parseLogType,
} from '../utils'

const OldValueChip: React.FC<{ value: string }> = ({ value }) => (
  <span className="line-through text-red-500">{value}</span>
)

const NewValueChip: React.FC<{ value: string }> = ({ value }) => (
  <span className="text-green-600">{value}</span>
)

// ---------------------------------------------------------------------------
// Description builder
// ---------------------------------------------------------------------------

const EXPAND_THRESHOLD = 80
const TRUNCATE_LENGTH = 43

const renderDeletedAction = (
  entry: EntityAuditLogEntry,
  resolvedEntityTypeKey: string,
  entityTypeLabel: string
) => {
  const resolvedLowercase = resolvedEntityTypeKey.toLowerCase()
  const entityKeyWithoutUnderscore = resolvedLowercase.replace(/_/g, '') //NOSONAR
  const entityName =
    get(entry, `${resolvedLowercase}.name`) ??
    get(entry, `${entityKeyWithoutUnderscore}.name`)

  return (
    <span>
      Deleted the {entityTypeLabel}
      {entityName ? <OldValueChip value={entityName as string} /> : null}
    </span>
  )
}

const renderDelegatedAction = (
  entry: EntityAuditLogEntry,
  meta: EntityAuditLogEntry['log_metadata'],
  resolvedEntityTypeKey: string,
  entityTypeLabel: string
) => {
  const delegatedToRaw = meta?.extra?.delegated_to as
    | { user_name?: string }
    | undefined
  const delegatedTo = delegatedToRaw?.user_name || ''
  const note = meta?.extra?.note

  const resolvedLowercase = resolvedEntityTypeKey.toLowerCase()
  const entityKeyWithoutUnderscore = resolvedLowercase.replace(/_/g, '') //NOSONAR
  const entityName =
    get(entry, `${resolvedLowercase}.name`) ??
    get(entry, `${entityKeyWithoutUnderscore}.name`) ??
    get(entry, `deliverable_set.deliverable_set_name`)

  const noteStr = typeof note === 'string' ? note : JSON.stringify(note)
  const suffix = note ? ` with note "${noteStr}"` : ''
  return (
    <span>
      Delegated {entityTypeLabel}{' '}
      {entityName ? (
        <span className="font-medium text-primary-900">{entityName}</span>
      ) : (
        ''
      )}{' '}
      to <span className="font-medium text-primary-900">{delegatedTo}</span>
      {suffix}
    </span>
  )
}

const renderApprovalDetails = (meta: EntityAuditLogEntry['log_metadata']) => {
  const reviewer = meta?.extra?.reviewer || meta?.extra?.reviewers
  const viewer = meta?.extra?.viewer || meta?.extra?.viewers
  const targetDate = meta?.extra?.target_date || meta?.extra?.due_date
  const note = meta?.extra?.note

  const displayReviewer = normalizeLogValue(reviewer) || ''
  const displayViewer = normalizeLogValue(viewer) || ''
  const displayTargetDate = normalizeLogValue(targetDate) || ''
  const displayNote = normalizeLogValue(note) || ''

  return (
    <div className="mt-1 flex flex-col text-sm text-secondary-600 gap-0.5">
      <div><span className="font-medium text-primary-900">Reviewer:</span> {displayReviewer.length > 0 ? displayReviewer : '-'}</div>
      <div><span className="font-medium text-primary-900">Viewer:</span> {displayViewer.length > 0 ? displayViewer : '-'}</div>
      <div><span className="font-medium text-primary-900">Target Date:</span> {displayTargetDate.length > 0 ? displayTargetDate : '-'}</div>
      <div><span className="font-medium text-primary-900">Note:</span> {displayNote.length > 0 ? displayNote : '-'}</div>
    </div>
  )
}

const renderRequestedAction = (
  meta: EntityAuditLogEntry['log_metadata'],
  t: TFunction
) => {
  const details = renderApprovalDetails(meta)
  return (
    <div className="flex flex-col">
      <span>{t('history.actions.requested')}</span>
      {details}
    </div>
  )
}

const renderCancelledAction = (
  meta: EntityAuditLogEntry['log_metadata'],
  t: TFunction
) => {
  const details = renderApprovalDetails(meta)
  return (
    <div className="flex flex-col">
      <span>{t('history.actions.cancelled')}</span>
      {details}
    </div>
  )
}

const renderUpdatedAction = (
  entry: EntityAuditLogEntry,
  meta: EntityAuditLogEntry['log_metadata'],
  entityTypeLabel: string
) => {
  if (!meta) return null

  const field = meta.field
  const oldValue = meta.old_value
  const newValue = meta.new_value
  const isSystem = entry.user?.id === SYSTEM_USER_ID

  if (field && oldValue != null && newValue != null) {
    const parsedOld = formatFieldValue(field, oldValue)
    const parsedNew = formatFieldValue(field, newValue)
    const hasLongContent =
      parsedOld.length + parsedNew.length > EXPAND_THRESHOLD

    return (
      <FieldChangeLine
        field={field}
        entityType={entityTypeLabel}
        oldValue={parsedOld}
        newValue={parsedNew}
        hasLongContent={hasLongContent}
        isSystem={isSystem}
      />
    )
  }

  if (field && newValue != null) {
    const parsedNew = formatFieldValue(field, newValue)
    return (
      <span>
        {isSystem ? 'Auto set' : 'Set'} the {formatField(field)}
        {entityTypeLabel ? ` of the ${entityTypeLabel}` : ''} to{' '}
        <NewValueChip value={parsedNew} />
      </span>
    )
  }
  return null
}

export const AuditCardDescription: React.FC<{ entry: EntityAuditLogEntry }> = ({
  entry,
}) => {
  const { t } = useTranslation()
  const meta = entry.log_metadata
  const { action, rawEntity } = parseLogType(entry.log_type)

  // 1. Resolve entity type dynamically (meta override > parsed from log_type)
  const resolvedEntityTypeKey = get(meta, 'type_entity', rawEntity)
  const entityTypeLabel = formatEntityType(resolvedEntityTypeKey)

  switch (action) {
    case AuditLogAction.CREATED:
      return <span>Created the {entityTypeLabel}</span>
    case AuditLogAction.DELETED:
      return renderDeletedAction(entry, resolvedEntityTypeKey, entityTypeLabel)
    case AuditLogAction.DELEGATED:
      return renderDelegatedAction(
        entry,
        meta,
        resolvedEntityTypeKey,
        entityTypeLabel
      )
    case AuditLogAction.REQUESTED:
      return renderRequestedAction(meta, t)
    case AuditLogAction.CANCELLED:
      return renderCancelledAction(meta, t)
    case AuditLogAction.UPDATED: {
      const rendered = renderUpdatedAction(entry, meta, entityTypeLabel)
      if (rendered) return rendered
      break
    }
  }

  // Fallback if no matching action or missing meta for UPDATED
  return <span>{formatLogType(entry.log_type)}</span>
}

interface FieldChangeLineProps {
  field: string
  entityType: string | null
  oldValue: string
  newValue: string
  hasLongContent: boolean
  isSystem?: boolean
}

const FieldChangeLine: React.FC<FieldChangeLineProps> = ({
  field,
  entityType,
  oldValue,
  newValue,
  hasLongContent,
  isSystem,
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const truncOld = expanded
    ? oldValue
    : truncate(oldValue, { length: TRUNCATE_LENGTH })
  const truncNew = expanded
    ? newValue
    : truncate(newValue, { length: TRUNCATE_LENGTH })

  return (
    <div>
      <span>
        {isSystem ? 'Auto changed' : 'Changed'} the {formatField(field)}
        {entityType ? ` of the ${entityType}` : ''} from{' '}
        <OldValueChip value={truncOld} /> to <NewValueChip value={truncNew} />
      </span>
      {hasLongContent && (
        <button
          type="button"
          className="text-sm text-brand-tertiary-600 font-medium hover:text-brand-tertiary-700 transition-colors border-none bg-transparent p-0 cursor-pointer mt-1 block"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t('common.seeLess') : t('common.seeMore')}
        </button>
      )}
    </div>
  )
}
